import { createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';

import type { MetadataMetadataValuesSchemaPatched } from '@iconik-io/iconik-api/src/schema_patched/metadata';

import { serverToReduxFormat } from '../../utils/format_metadata_values';
import type { MetadataValuesAppState } from './types';

const initialState: MetadataValuesAppState = {
  metadataValues: {},
  awaitingMetadataValues: {},
};

const applyValues = (
  values: Draft<MetadataValuesAppState>['metadataValues'],
  payload: MetadataMetadataValuesSchemaPatched,
) => {
  const objectId = payload.object_id;
  if (!objectId) {
    return;
  }

  const updatedValues = serverToReduxFormat(payload.metadata_values);
  if (Object.keys(updatedValues).length) {
    values[objectId] = {
      ...values[objectId],
      ...updatedValues,
    };
  } else {
    values[objectId] = {};
  }
};

const receiveAssetValues = (
  state: Draft<MetadataValuesAppState>,
  action: PayloadAction<MetadataMetadataValuesSchemaPatched>,
) => {
  applyValues(state.metadataValues, action.payload);
  state.awaitingMetadataValues = {};
};

export const metadataValuesSlice = createSlice({
  name: 'metadata_values',
  initialState,
  reducers: {
    metadataValuesGetAsset: receiveAssetValues,
    metadataValuesPutAsset: receiveAssetValues,
    metadataValuesCreateAwaiting: (
      state,
      action: PayloadAction<MetadataMetadataValuesSchemaPatched>,
    ) => {
      state.awaitingMetadataValues = { ...state.metadataValues };
      applyValues(state.awaitingMetadataValues, action.payload);
    },
    metadataValuesAddAwaiting: (state) => {
      Object.assign(state.metadataValues, state.awaitingMetadataValues);
      state.awaitingMetadataValues = {};
    },
    metadataValuesClearById: (state, action: PayloadAction<string>) => {
      state.metadataValues[action.payload] = {};
    },
  },
});

export const {
  metadataValuesGetAsset,
  metadataValuesPutAsset,
  metadataValuesCreateAwaiting,
  metadataValuesAddAwaiting,
  metadataValuesClearById,
} = metadataValuesSlice.actions;

export const metadataValuesReducer = metadataValuesSlice.reducer;
